import React, { useState, useEffect, useRef } from 'react';
import { useDS5Controller } from '../hooks/useDS5Controller';
import { useMovementCache } from '../hooks/useMovementCache';
import { api } from '../services/api';
import './GradingStation.css';
import packageJson from '../../package.json';
import Cadance from './Cadance';
import { useDS5Status } from '../hooks/useDS5Status';

const APP_VERSION = packageJson.version;

const GradingStation = ({
  userInfo,
  userPosition,
  stationName = "Grading and Quality Control",
  nextJobOnRepair = "Repair"
}) => {
  const { serialNumber, isConnected, ds5color } = useDS5Controller();
  const movementsCache = useMovementCache();
  const { ds5Status, updateDS5Status, resetDS5Status } = useDS5Status();

  const [warning, setWarning] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [nextJob, setNextJob] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasBeenProcessed, setHasBeenProcessed] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [currentMovementData, setCurrentMovementData] = useState(null);
  const [hourcount, setHourCount] = useState(0);

  const [showFailModal, setShowFailModal] = useState(false);
  const [pannes, setPannes] = useState([]);
  const [selectedPanneId, setSelectedPanneId] = useState('');
  const [selectedPanneInfo, setSelectedPanneInfo] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [availableMovements, setAvailableMovements] = useState([]);
  const [selectedMovementId, setSelectedMovementId] = useState('');
  const [modalError, setModalError] = useState('');

  const isCheckingRef = useRef(false);
  const prevSerialNumberRef = useRef('');
  const hasStatusBeenCheckedRef = useRef(false);
  const statusInitialLoadRef = useRef(true);
  const [statusCheckCompleted, setStatusCheckCompleted] = useState(false);

  const isCorrectStation = userPosition === stationName;
  const isLockValid = currentMovementData && currentMovementData.nextjob === stationName && userPosition === stationName;

  // --- Hourly counter reset ---
  useEffect(() => {
    const resetHourlyCount = () => setHourCount(0);
    const now = new Date();
    const msUntilNextHour = (60 - now.getMinutes()) * 60 * 1000 - now.getSeconds() * 1000 - now.getMilliseconds();

    const timeoutId = setTimeout(() => {
      resetHourlyCount();
      const intervalId = setInterval(resetHourlyCount, 60 * 60 * 1000);
      return () => clearInterval(intervalId);
    }, msUntilNextHour);

    return () => clearTimeout(timeoutId);
  }, []);

  // --- Load pannes & movements on fail modal ---
  useEffect(() => {
    if (!showFailModal) return;

    Promise.all([
      api.getAllDs5Panne(),
      api.getAllMovements ? api.getAllMovements() : Promise.resolve([])
    ])
      .then(([pannesData, movementsData]) => {
        const excludedPannes = ['Rayure légere', 'Grattage léger', 'Rayure profonde', 'Grattage profond'];
        const filteredPannes = Array.isArray(pannesData)
          ? pannesData.filter(p => !excludedPannes.includes(p.panne || p.name))
          : [];
        setPannes(filteredPannes);

        if (Array.isArray(movementsData) && movementsData.length > 0) {
          setAvailableMovements(movementsData);
        } else {
          const movementsFromCache = Object.entries(movementsCache || {}).map(([name, id]) => ({ id, name }));
          setAvailableMovements(movementsFromCache);
        }
      })
      .catch(err => {
        setPannes([]);
        setAvailableMovements([]);
        setWarning('Erreur chargement des données : ' + err.message);
      });
  }, [showFailModal, movementsCache]);

  // --- Utility functions ---
  const getMovementNameById = (movementId) => {
    if (!movementsCache || Object.keys(movementsCache).length === 0) return null;
    return Object.entries(movementsCache).find(([_, id]) => id === movementId)?.[0] || null;
  };

  const getNextJobNameFromId = (nextJobId) => nextJobId ? getMovementNameById(nextJobId) : null;

  // --- Check DS5 history ---
  useEffect(() => {
    if (!isConnected || !serialNumber || serialNumber.includes('Error')) {
      setWarning('');
      setCurrentMovementData(null);
      setHasBeenProcessed(false);
      setSelectedGrade('');
      resetDS5Status();
      hasStatusBeenCheckedRef.current = false;
      statusInitialLoadRef.current = true;
      setStatusCheckCompleted(false);
      return;
    }
    if (!movementsCache || Object.keys(movementsCache).length === 0) return;

    const checkMovementHistory = async () => {
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;

      try {
        const data = await api.getCurrentMovement(serialNumber);
        if (!data) {
          setWarning("Controller not in database - Start with Quick Drift");
          setHasBeenProcessed(false);
          return;
        }

        const processedData = {
          ...data,
          movement: getMovementNameById(data.id_movement),
          nextjob: getMovementNameById(data.id_nextjob),
          id_movement: data.id_movement,
          id_nextjob: data.id_nextjob
        };

        setCurrentMovementData(processedData);

        if (processedData.nextjob !== stationName) {
          setWarning(`Next_Job : ${processedData.nextjob || 'Aucun'}`);
          setHasBeenProcessed(false);
          return;
        }

        if (userPosition !== stationName) {
          setWarning(`Position incorrecte: ${userPosition} - Doit être "${stationName}"`);
          setHasBeenProcessed(false);
          return;
        }

        setWarning('');
      } catch (err) {
        console.error(err);
        setWarning(err.message.includes('404')
          ? "Controller not in database - Start with Quick Drift"
          : "Erreur de vérification de l'historique");
        setHasBeenProcessed(false);
      } finally {
        isCheckingRef.current = false;
      }
    };

    checkMovementHistory();
  }, [isConnected, serialNumber, movementsCache, userPosition, stationName]);

  // --- DS5 status update ---
  useEffect(() => {
    const serialChanged = serialNumber !== prevSerialNumberRef.current;

    if (isConnected && serialNumber && !serialNumber.includes('Error') && serialChanged && isLockValid && !hasStatusBeenCheckedRef.current) {
      prevSerialNumberRef.current = serialNumber;
      hasStatusBeenCheckedRef.current = true;
      statusInitialLoadRef.current = true;
      updateDS5Status(serialNumber);
    } else if ((!isConnected || !serialNumber || serialNumber.includes('Error')) && prevSerialNumberRef.current) {
      prevSerialNumberRef.current = '';
      resetDS5Status();
      hasStatusBeenCheckedRef.current = false;
      statusInitialLoadRef.current = true;
      setStatusCheckCompleted(false);
    }
  }, [isConnected, serialNumber, updateDS5Status, resetDS5Status, isLockValid]);

  useEffect(() => {
    if (ds5Status.isLoading) return;
    if (statusCheckCompleted) return;

    if (statusInitialLoadRef.current && !ds5Status.isLoading && ds5Status.status !== 'unknown') {
      statusInitialLoadRef.current = false;
      setStatusCheckCompleted(true);
    }
  }, [ds5Status.isLoading, ds5Status.status, statusCheckCompleted]);
  const handlePanneSelection = (panneId) => {
    const panne = pannes.find(p => p.id === Number(panneId));
    setSelectedPanneInfo(panne || null);
  };
  // --- Save to database ---
  const saveToDatabase = async (grade, targetNextJob, panneId) => {
    if (!isLockValid) return false;

    const movementId = movementsCache[stationName];
    const nextJobId = movementsCache[targetNextJob];

    if (!movementId || !nextJobId) {
      setWarning("Mouvement non configuré dans le cache");
      return false;
    }

    const payload = {
      serial_number: serialNumber,
      operator: userInfo?.id || 1,
      tester: stationName,
      movement: movementId,
      nextjob: nextJobId,
      Grade: grade,
      panne: panneId ? Number(panneId) : null,
      version: APP_VERSION,
      color:ds5color
    };

    try {
      setIsSaving(true);
      await api.saveMovement(payload);
      setNextJob(targetNextJob);
      setHourCount(prev => prev + 1);
      setSelectedGrade(grade || '-');
      setShowSuccess(true);
      setHasBeenProcessed(true);
      setWarning('');
      return true;
    } catch (err) {
      setWarning("Erreur enregistrement : " + err.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // --- Prepare confirmation for fail ---
  const prepareConfirmation = () => {
    setModalError('');

    if (!selectedPanneId) {
      setModalError("Veuillez sélectionner un type de panne");
      return;
    }

    if (!selectedMovementId) {
      setModalError("Veuillez sélectionner un mouvement de destination");
      return;
    }

    const selectedMovement = availableMovements.find(m => m.id === Number(selectedMovementId));
    const movementName = selectedMovement ? selectedMovement.name : null;

    if (!movementName) {
      setModalError("Mouvement sélectionné invalide");
      return;
    }

    const confirmationData = {
      panneText: selectedPanneInfo?.panne || selectedPanneInfo?.name || '',
      grade: selectedPanneInfo?.grade || null,
      target: movementName
    };

    setConfirmationData(confirmationData);
    setShowFailModal(false);

    setTimeout(() => setShowConfirmationModal(true), 50);
  };

  // --- Confirm fail ---
  const handleFailConfirm = async () => {
    if (!confirmationData) return;

    await saveToDatabase(confirmationData.grade, confirmationData.target, selectedPanneId);

    setShowConfirmationModal(false);
    setSelectedPanneId('');
    setSelectedPanneInfo(null);
    setSelectedMovementId('');
    setConfirmationData(null);
    setModalError('');
  };

  // --- DS5 Status render ---
  const renderDS5Status = () => {
    if (ds5Status.isLoading) return <div className="ds5-status loading">Checking Full Test status...</div>;

    const statusConfig = {
      ok: { text: 'PASS', color: '#198754', className: 'pass' },
      ko: { text: 'FAIL', color: '#dc3545', className: 'fail' },
      error: { text: 'Connection Error', color: '#6c757d', className: 'error' },
      unknown: { text: 'Not Tested', color: '#ffc107', className: 'unknown' }
    };

    const config = statusConfig[ds5Status.status] || statusConfig.unknown;

    return (
      <div className={`ds5-status ${config.className}`}>
        <div>Status Full Test: <strong style={{ color: config.color }}>{config.text}</strong></div>
        {ds5Status.date && ds5Status.date !== 'unknown' && <div>Test Date: {ds5Status.date}</div>}
        {ds5Status.message && <div>{ds5Status.message}</div>}
      </div>
    );
  };

  // --- Reset on serial change ---
  useEffect(() => {
    setShowSuccess(false);
    setNextJob('');
    setCurrentMovementData(null);
    setWarning('');
    setHasBeenProcessed(false);
    setSelectedGrade('');
  }, [serialNumber]);

  if (!isCorrectStation) return null;

  return (
    <div className="grading-container">
      <Cadance userInfo={userInfo} movementId={5} hourCount={hourcount} />

      <div className="grading-card">
        <div className="grading-header"><h1>{stationName}</h1></div>

        {!isConnected ? (
          <div className="disconnected"><h1>🔌 Plug in your DualSense controller.</h1></div>
        ) : (
          <>
            <div className="serial-display">
              <div className="serial-row">
                <label>Serial Number:</label>
                <div className="serial-value">{serialNumber}</div>
              </div>

              <div className="serial-row">
                <label>Color:</label>
                <div className="color-value">{ds5color}</div>
              </div>
            </div>

            {renderDS5Status()}
            {warning && <div className="message warning-message">{warning}</div>}
            {isSaving && <div className="message waiting-message">Enregistrement...</div>}

            {showSuccess && nextJob && (
              <div className={`message success-message permanent ${nextJob === 'Repair' ? 'fail' : 'pass'}`}>
                <div><strong>Next Job:</strong> {nextJob}</div>
                {selectedGrade !== '-' && <div><strong>Grade:</strong> {selectedGrade}</div>}
              </div>
            )}

            {!hasBeenProcessed && isLockValid && !isSaving && !showSuccess && userInfo && ds5Status.status === 'ok' && (
              <div>
                <div className="grade-buttons-row">
                  <button onClick={() => setSelectedGrade('A')} className={`btn-grade grade-a ${selectedGrade === 'A' ? 'selected' : ''}`}>A</button>
                  <button onClick={() => setSelectedGrade('B')} className={`btn-grade grade-b ${selectedGrade === 'B' ? 'selected' : ''}`}>B</button>
                  <button onClick={() => setSelectedGrade('C')} className={`btn-grade grade-c ${selectedGrade === 'C' ? 'selected' : ''}`}>C</button>
                </div>

                <div className="action-buttons-row">
                  <button onClick={() => selectedGrade && saveToDatabase(selectedGrade, 'OOB', null)} disabled={!selectedGrade || isSaving} className="btn-validate-grade">VALIDATE GRADE</button>
                  <button onClick={() => { setShowFailModal(true); setSelectedPanneId(''); setSelectedPanneInfo(null); setSelectedMovementId(''); setModalError(''); }} disabled={isSaving} className="btn-fail">FAIL</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* --- Confirmation Modal --- */}
      {showConfirmationModal && confirmationData && (
        <div className="fail-modal-overlay" onClick={() => setShowConfirmationModal(false)}>
          <div className="fail-modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
            <h2>Confirmation</h2>
            <div className="confirmation-details">
              <div className="confirmation-item"><strong>Panne:</strong> {confirmationData.panneText}</div>
              <div className="confirmation-item"><strong>Next Job:</strong> {confirmationData.target}</div>
            </div>
            <div className="confirmation-warning">Vérifiez bien ces informations avant de confirmer !</div>
            <div className="modal-buttons">
              <button onClick={handleFailConfirm} disabled={isSaving} className="btn-confirm">{isSaving ? 'Enregistrement...' : 'Confirmer'}</button>
              <button onClick={() => setShowConfirmationModal(false)} className="btn-cancel" disabled={isSaving}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* --- Fail Modal --- */}
      {showFailModal && (
        <div className="fail-modal-overlay" onClick={() => { setShowFailModal(false); setModalError(''); setSelectedPanneId(''); setSelectedPanneInfo(null); setSelectedMovementId(''); }}>
          <div className="fail-modal-content" onClick={e => e.stopPropagation()}>
            <h2>Déclaration de Non-Conformité</h2>
            {modalError && <div className="message error-message modal-error">{modalError}</div>}

            <div className="modal-field">
              <label>Sélectionnez le type de panne:</label>
              <select value={selectedPanneId} onChange={e => { setSelectedPanneId(e.target.value); handlePanneSelection(e.target.value); setModalError(''); }}>
                <option value="">-- Choisir une panne --</option>
                {pannes.map(p => (<option key={p.id} value={p.id}>{p.panne || p.name}</option>))}
              </select>
            </div>

            <div className="modal-field">
              <label>Sélectionnez le NextJob de destination:</label>
              <select value={selectedMovementId} onChange={e => { setSelectedMovementId(e.target.value); setModalError(''); }}>
                <option value="">-- Choisir un NextJob --</option>
                {availableMovements.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}
              </select>
            </div>

            {selectedPanneInfo && (
              <div className="panne-info-display">
                <div className="info-item"><strong>Panne sélectionnée:</strong> {selectedPanneInfo.panne || selectedPanneInfo.name}</div>
                <div className="info-item"><strong>NextJob choisi:</strong> {availableMovements.find(m => m.id === Number(selectedMovementId))?.name || 'Non sélectionné'}</div>
              </div>
            )}

            <div className="modal-buttons">
              <button onClick={prepareConfirmation} className="btn-confirm">Confirmer</button>
              <button onClick={() => setShowFailModal(false)} className="btn-cancel">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GradingStation;
